import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop'
import { AsyncPipe } from '@angular/common'
import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { TranslatePipe } from '@ngx-translate/core'
import { BehaviorSubject, lastValueFrom } from 'rxjs'
import { MeetingResourceCitation, MeetingSentenceCitation } from '../../../../../../backend/src/citations/minutes-citations-api.dto'
import { ToastService } from '../../../toast.service'
import { OpenDocumentEvent } from '../extensions/citation-plugin'
import { AiActionService } from './ai-action.service'
import { FactCheckResolution, LLM_LIVE_DEFAULT_TIMEOUT_SECONDS, LLM_LIVE_DEFAULT_WARNING_SECONDS, LlmAction, SentenceIdPair } from './ai-action.types'

export type AiActionFactCheckDialogData = {
  meetingId: number
  action: LlmAction
  actionDisplayName: string
  selectionContent: string
  sentenceIdPairs: SentenceIdPair[]
  templateItemIds: number[]
  existingItemContent: string | null
  onOpenDocument: (event: OpenDocumentEvent) => void
  onOpenMeeting: (event: { startSentenceId: string; endSentenceId: string }) => void
}

export type AiActionFactCheckDialogResult = {
  insertionText: string
}

const ANSWER_TRANSLATION_KEYS: Record<FactCheckResolution['answer'], string> = {
  True: 'EDITOR.AI_ACTION_DIALOG.TRUE',
  False: 'EDITOR.AI_ACTION_DIALOG.FALSE',
  Unclear: 'EDITOR.AI_ACTION_DIALOG.UNCLEAR',
}

@Component({
  selector: 'app-ai-action-fact-check-dialog',
  standalone: true,
  imports: [AsyncPipe, CdkDrag, CdkDragHandle, MatButtonModule, MatDialogModule, MatIconModule, MatProgressSpinnerModule, TranslatePipe],
  templateUrl: './ai-action-fact-check-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ai-action-fact-check-dialog.component.scss',
})
export class AiActionFactCheckDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<AiActionFactCheckDialogComponent>)
  data: AiActionFactCheckDialogData = inject(MAT_DIALOG_DATA)
  private aiActionService = inject(AiActionService)
  private toastService = inject(ToastService)

  resolutions$ = new BehaviorSubject<FactCheckResolution[] | null>(null)
  showInsertButton = false
  private citationCountsBefore: number[] = []
  private slowWarningTimeoutId: ReturnType<typeof setTimeout> | null = null

  ngOnInit() {
    this.slowWarningTimeoutId = setTimeout(() => {
      this.toastService.show('EDITOR.AI_ACTION_SLOW')
    }, LLM_LIVE_DEFAULT_WARNING_SECONDS * 1000)

    Promise.race<FactCheckResolution[] | null>([
      lastValueFrom(
        this.aiActionService.sendSyncRequest(
          this.data.meetingId,
          this.data.action,
          this.data.sentenceIdPairs,
          this.data.selectionContent,
          this.data.templateItemIds,
          this.data.existingItemContent
        )
      ),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), LLM_LIVE_DEFAULT_TIMEOUT_SECONDS * 1000)),
    ])
      .then((resolutions) => {
        this.clearTimeouts()

        if (resolutions === null) {
          this.toastService.show('EDITOR.AI_ACTION_FAILED')
          this.dialogRef.close(null)
          return
        }

        this.showInsertButton = resolutions.some((resolution) => resolution.answer === 'False' && resolution.newWording !== null)

        const counts = [0]
        for (let index = 0; index < resolutions.length; index++) {
          counts.push(counts[index] + resolutions[index].citations.length)
        }
        this.citationCountsBefore = counts

        this.resolutions$.next(resolutions)
      })
      .catch(() => {
        this.clearTimeouts()
        this.toastService.show('EDITOR.AI_ACTION_FAILED')
        this.dialogRef.close(null)
      })
  }

  ngOnDestroy() {
    this.clearTimeouts()
  }

  getAnswerTranslationKey(answer: FactCheckResolution['answer']): string {
    return ANSWER_TRANSLATION_KEYS[answer]
  }

  getCitationIndex(resolutionIndex: number, citationIndex: number): number {
    return this.citationCountsBefore[resolutionIndex] + citationIndex + 1
  }

  onCitationClick(citation: MeetingSentenceCitation | MeetingResourceCitation) {
    if (citation.typename === 'MeetingResourceCitation') {
      this.data.onOpenDocument({
        resourceId: citation.resourceId,
        resourcePageNumber: citation.resourcePageNumber,
        citedTexts: citation.citedTexts,
      })
    } else {
      this.data.onOpenMeeting({
        startSentenceId: citation.startSentenceId,
        endSentenceId: citation.endSentenceId,
      })
    }
  }

  accept() {
    const resolutions = this.resolutions$.getValue()
    if (resolutions === null) {
      return
    }
    const insertionText = this.formatInsertionContent(resolutions)
    const result: AiActionFactCheckDialogResult = { insertionText }
    this.dialogRef.close(result)
  }

  close() {
    this.dialogRef.close(null)
  }

  private clearTimeouts() {
    if (this.slowWarningTimeoutId !== null) {
      clearTimeout(this.slowWarningTimeoutId)
      this.slowWarningTimeoutId = null
    }
  }

  private formatInsertionContent(resolutions: FactCheckResolution[]): string {
    if (resolutions.length === 1 && resolutions[0].newWording !== null) {
      return resolutions[0].newWording
    }
    return resolutions
      .map((resolution) => {
        if (resolution.answer === 'True' || resolution.answer === 'Unclear') {
          return resolution.statement
        }
        return resolution.newWording ?? resolution.statement
      })
      .join(' ')
  }
}
